import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';
const SchoolValuation = () => {
  usePageTitle('School Valuation & Acquisition Services');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">School Management Services – Valuation & Acquisition Services</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/school (7).jpg' 
            alt="School Valuation & Acquisition" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              In today's dynamic educational ecosystem, schools are no longer just centers of learning — they are 
              high-value institutions with assets, intellectual capital, and brand equity.
            </p>
            <p className="text-lg line-height-3 mt-3">
              Whether you are looking to buy, sell, invest in, or merge with a school, understanding the true value 
              of the institution is critical. At SnP Finvisors, we offer comprehensive Valuation & Acquisition Services 
              tailored specifically for schools, educational trusts, and institutional investors.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              We combine financial expertise with deep education-sector knowledge to provide accurate valuations, 
              identify acquisition opportunities, and facilitate smooth transactions while ensuring legal, financial, 
              and reputational compliance.
            </p>
          </Card>
        </div>

        {/* Unique Valuation Section */}
        <div className="col-12 mt-5">
          <h2>What Makes School Valuation Unique?</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Unlike traditional businesses, the valuation of a school goes beyond financial numbers. It involves 
                intangible factors like academic reputation, alumni base, student-teacher ratios, affiliation history, 
                location, infrastructure quality, and potential for scalability.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold text-primary">
                Our approach considers all of these — ensuring that your investment or divestment decision is based 
                on the complete picture.
              </p>
            </div>
            <img 
              src='/images/school (3).jpg' 
              alt="School Valuation Factors" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Core Offerings Section */}
        <div className="col-12 mt-5">
          <h2>Our Core Offerings</h2>
          <Divider />
          
          <div className="grid">
            {/* Offering 1 */}
            <div className="col-12">
              <Card title="1. School Valuation Services" className="mb-4">
                <div className="grid">
                  <div className="col-12 md:col-4">
                    <h4 className="mt-0">Financial Valuation</h4>
                    <p>Evaluation of revenue streams (tuition, transport, donations, hostel, etc.), cost structures, and profitability</p>
                  </div>
                  <div className="col-12 md:col-4">
                    <h4 className="mt-0">Asset-Based Valuation</h4>
                    <p>Estimation of tangible asset value – land, buildings, labs, libraries, digital infrastructure</p>
                  </div>
                  <div className="col-12 md:col-4">
                    <h4 className="mt-0">Brand & Goodwill Valuation</h4>
                    <p>Assessment of the institution's market reputation, academic track record, alumni perception, and demand</p>
                  </div>
                  <div className="col-12 md:col-6">
                    <h4 className="mt-0">Affiliation & Licensure Review</h4>
                    <p>Ensuring value is not compromised due to regulatory gaps or expired approvals</p>
                  </div>
                  <div className="col-12 md:col-6">
                    <h4 className="mt-0">Future-Proofing Valuation</h4>
                    <p>Projection-based modeling for institutions with growth potential (e.g., scope for new branches, increasing student strength)</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Offering 2 */}
            <div className="col-12 md:col-6">
              <Card title="2. Buy-Side Advisory (School Acquisition)" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Target Identification: We help buyers identify schools that meet their strategic, financial, and geographic goals</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Due Diligence: Conduct legal, financial, operational, and commercial due diligence</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Valuation Validation: Cross-checking seller's valuation with our independent assessment</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Deal Structuring: Advisory on payment structure, earn-outs, equity participation, and transition management</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Negotiation Support: Representing buyer interests during negotiations and facilitating documentation</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Offering 3 */}
            <div className="col-12 md:col-6">
              <Card title="3. Sell-Side Advisory (School Sale or Stake Dilution)" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Pre-Sale Readiness: Enhancing school valuation by fixing compliance issues and improving reporting</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Investor/Buyer Matching: Identifying potential investors who align with your school's mission</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Information Memorandum (IM): Preparation of detailed investor documents highlighting school achievements</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Confidential Deal Execution: Ensuring sensitive information is protected during negotiations</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Offering 4 */}
            <div className="col-12 md:col-6">
              <Card title="4. Mergers & Strategic Alliances" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advisory on strategic collaborations, mergers of educational trusts, or resource-sharing models</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Structuring academic, legal, and administrative synergies between merging institutions</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Monitoring post-merger integration and stakeholder alignment</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Offering 5 */}
            <div className="col-12 md:col-6">
              <Card title="5. Post-Transaction Support" className="mb-4">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Handholding during transition phase—HR alignment, academic continuity, regulatory updating</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Re-branding assistance, communication strategy for students and parents, and PR control</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="col-12 mt-5">
          <h2>Why SnP Finvisors?</h2>
          <Divider />
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Sector-Specific Expertise</h3>
                <p>
                  Unlike generic valuation firms, we understand schools inside out—from affiliations and pedagogy to fees and faculty.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Holistic Approach</h3>
                <p>
                  We go beyond spreadsheets and include real-world insights from academic performance, operations, and community reputation.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Network Access</h3>
                <p>
                  Our rich network includes education entrepreneurs, investors, and trusts looking for deals.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">End-to-End Support</h3>
                <p>
                  From valuation to negotiation, documentation to integration—we support you at every step.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Confidential & Ethical</h3>
                <p>
                  We maintain the highest level of confidentiality and compliance across all transaction stages.
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* Who Can Benefit Section */}
        <div className="col-12 mt-5">
          <h2>Who Can Benefit?</h2>
          <Divider />
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-users text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Educational trusts planning to exit or expand</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-user text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Entrepreneurs seeking to acquire a school in a strategic location</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-briefcase text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Corporates investing in education for CSR or diversification</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-money-bill text-primary mr-3 text-xl"></i>
                <span className="font-semibold">HNIs and family offices entering the education sector</span>
              </div>
            </div>
            <div className="col-12">
              <div className="flex align-items-center">
                <i className="pi pi-sitemap text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Schools looking to merge or partner for operational efficiencies</span>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <h2>Empowering Decisions with Clarity & Confidence</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we turn complex decisions around school acquisitions and sales into well-informed, 
                transparent, and strategically sound actions. Whether you are expanding your footprint or planning 
                a graceful exit, we ensure that the entire process is efficient, compliant, and value-driven.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                Speak to our valuation experts and uncover the real worth of your school or your next potential investment.
              </p>
            </div>
            <img 
              src='/images/school (5).jpg' 
              alt="School Valuation Experts" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolValuation;